/**
 * The ASS/SSA override-tag stream: `{\i1\pos(320,240)}text{\k25}syl`.
 *
 * THE ONE STRUCTURAL DECISION, and it is the contract's, frozen in §5: `InlineStyle` is
 * **RESOLVED, NOT CUMULATIVE**. An ASS tag stream is inherently cumulative — `{\i1}` stays on
 * until `{\i0}` or `{\r}` — so this expander walks the stream carrying running state and emits
 * each run's COMPLETE effective delta from the cue's Style. Run N is never interpreted against
 * run N−1 anywhere downstream, which is what lets reflow, merge and split move runs around
 * without rendering changing.
 *
 * KARAOKE. `{\k25}` is a DURATION in centiseconds applied to the syllable that FOLLOWS it. The
 * model stores ABSOLUTE spans, so a cursor walks forward from the cue start: absolute means
 * merge is concat with no rebasing and a global shift is one +delta everywhere. `\kt`
 * repositions the cursor absolutely, which is the only tag that can move it backwards.
 *
 * TIME-BEARING TAGS ARE TYPED. `\fad`, `\move`, `\t` and `\fade` carry times, and the model
 * types them as `TickDelta` so the time walkers rescale them. An unrecognised tag becomes an
 * `UnknownTag` with `argsUnclassified: true`, which is what makes `mapTimes` emit
 * 'animation-time-not-rescaled' instead of silently desyncing a typeset sign — a named
 * warning rather than a wrong file.
 *
 * DRAWINGS. `{\p1}m 0 0 l 100 0…{\p0}` is a vector path. It goes to `DrawingSpec.commands` and
 * the run's `text` is `''`, by the frozen rule — so a cleaner can never mangle a path into a
 * black smear, and `plainText` can never leak path commands into dedupe or diff.
 */

import type {
  Alignment,
  Animation,
  ClipSpec,
  Color,
  FadeSpec,
  IdAllocator,
  InlineStyle,
  MoveSpec,
  Point,
  Position,
  Resolution,
  RunTimeKind,
  Style,
  TagArg,
  TextLine,
  TextRun,
  TickDelta,
  Ticks,
  UnknownTag,
  WrapStyle,
} from '../../../contracts/cue-model.ts';
import { makeLine, makeRun } from '../../core/model/build.ts';
import { T } from '../../core/time/ticks.ts';
import { parseAssColor } from '../common/color.ts';

/** Centiseconds → ticks. Exact on the 120 000 lattice. */
const CS = 1200;
/** Milliseconds → ticks. `\fad` and `\t` argue in milliseconds, not centiseconds. */
const MSTICKS = 120;

export interface TagParseInput {
  readonly text: string;
  readonly alloc: IdAllocator;
  readonly cueStart: Ticks;
  readonly cueEnd: Ticks;
  /** The cue's resolved Style — the baseline every delta is relative to, and the source of an
   *  RGB value when a `\1a` alpha override arrives with no colour override beside it. */
  readonly style: Style;
  /** For normalising `\pos`/`\move`/`\org`/`\clip` coordinates to 0–1. */
  readonly resolution: Resolution;
  /** Style lookup for `\rStyleName`. */
  readonly styleByName: (name: string) => Style | undefined;
}

export interface TagParseOutput {
  readonly lines: readonly TextLine[];
  readonly position: Position | null;
  readonly fade: FadeSpec | null;
  readonly wrap: WrapStyle | null;
  /** Present when any `\k`-family tag appeared — drives `Cue.karaoke` derivation. */
  readonly karaokeKind: 'k' | 'kf' | 'ko' | null;
  /** Sum of `\k` durations in ticks, for the coverage check against the cue duration. */
  readonly karaokeTicks: number;
  readonly granularity: 'coalesced' | 'word' | 'syllable';
  readonly hasFragileTags: boolean;
  readonly hasRelativeTimes: boolean;
  /** Tag names this expander did not model, for a 'malformed-tag'/'unknown-tag-args' report. */
  readonly unknownNames: readonly string[];
}

const WRAP_STYLES: readonly WrapStyle[] = ['smart-top', 'end-of-line', 'none', 'smart-bottom'];

/** SSA v4's `\a` alignment scheme → the ASS numpad. 1/2/3 bottom, +4 top, +8 middle. */
export function ssaAlignmentToNumpad(a: number): Alignment | null {
  switch (a) {
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return 3;
    case 5:
      return 7;
    case 6:
      return 8;
    case 7:
      return 9;
    case 9:
      return 4;
    case 10:
      return 5;
    case 11:
      return 6;
    default:
      return null;
  }
}

export function numpadToSsaAlignment(a: Alignment): number {
  switch (a) {
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return 3;
    case 4:
      return 9;
    case 5:
      return 10;
    case 6:
      return 11;
    case 7:
      return 5;
    case 8:
      return 6;
    case 9:
      return 7;
  }
}

const EMPTY_POSITION: Position = {
  alignment: null,
  point: null,
  move: null,
  rotationOrigin: null,
  clip: null,
  sizePct: null,
  vertical: null,
  line: null,
  positionPct: null,
};

interface RunState {
  style: InlineStyle;
  /** Alpha overrides pending merge into their colour. ASS lets `\1a` arrive without `\1c`. */
  alpha: { p?: number; s?: number; o?: number; b?: number };
  drawingScale: number;
  drawingBaselineOffset: number;
  inDrawing: boolean;
}

export function parseAssTags(input: TagParseInput): TagParseOutput {
  const { text, alloc, style, resolution } = input;

  const lines: TextLine[] = [];
  let runs: TextRun[] = [];
  let breakBefore: TextLine['breakBefore'] = 'none';
  let buf = '';

  const state: RunState = {
    style: {},
    alpha: {},
    drawingScale: 1,
    drawingBaselineOffset: 0,
    inDrawing: false,
  };

  let position: Position = EMPTY_POSITION;
  let positionTouched = false;
  let fade: FadeSpec | null = null;
  let wrap: WrapStyle | null = null;
  let karaokeKind: 'k' | 'kf' | 'ko' | null = null;
  let karaokeTicks = 0;
  let anyKaraoke = false;
  let hasFragile = false;
  let hasRelative = false;
  const unknownNames: string[] = [];

  /** Absolute karaoke cursor. Starts at the cue start; `\kt` moves it, `\k` advances it. */
  let cursor = input.cueStart as number;
  /** The pending syllable: set by a `\k` tag, consumed by the text that follows it. */
  let pending: { kind: RunTimeKind; ticks: number } | null = null;

  const resolvedStyle = (): InlineStyle => {
    let out: InlineStyle = { ...state.style };
    if (state.alpha.p !== undefined) {
      out = {
        ...out,
        primaryColor: withAlpha(out.primaryColor ?? style.primaryColor, state.alpha.p),
      };
    }
    if (state.alpha.s !== undefined) {
      out = {
        ...out,
        secondaryColor: withAlpha(out.secondaryColor ?? style.secondaryColor, state.alpha.s),
      };
    }
    if (state.alpha.o !== undefined) {
      out = {
        ...out,
        outlineColor: withAlpha(out.outlineColor ?? style.outlineColor, state.alpha.o),
      };
    }
    if (state.alpha.b !== undefined) {
      out = { ...out, shadowColor: withAlpha(out.shadowColor ?? style.shadowColor, state.alpha.b) };
    }
    return out;
  };

  const flush = (): void => {
    if (buf === '') return;
    if (state.inDrawing) {
      runs.push(
        makeRun({
          id: alloc.run(),
          kind: 'drawing',
          text: '',
          drawing: {
            commands: buf,
            scale: state.drawingScale,
            baselineOffset: state.drawingBaselineOffset,
          },
          style: resolvedStyle(),
        }),
      );
      buf = '';
      return;
    }
    const timing = pending;
    pending = null;
    if (timing !== null) {
      const start = cursor;
      const end = cursor + timing.ticks;
      cursor = end;
      karaokeTicks += timing.ticks;
      hasRelative = true;
      runs.push(
        makeRun({
          id: alloc.run(),
          text: buf,
          time: { start: T.ticks(start), end: T.ticks(end) },
          timeKind: timing.kind,
          timeOrigin: 'file',
          style: resolvedStyle(),
          // A karaoke syllable is SUB-word: "Twin"+"kle" are two runs of one word. Only the
          // first starts a word, and this is not derivable from whitespace, which is exactly
          // why `startsWord` exists as a field.
          startsWord: !continuesWord(runs),
        }),
      );
      buf = '';
      return;
    }
    runs.push(makeRun({ id: alloc.run(), text: buf, style: resolvedStyle() }));
    buf = '';
  };

  const newLine = (kind: TextLine['breakBefore']): void => {
    flush();
    lines.push(makeLine(runs, { breakBefore }));
    runs = [];
    breakBefore = kind;
  };

  let i = 0;
  while (i < text.length) {
    const ch = text[i] as string;

    if (ch === '\\' && i + 1 < text.length) {
      const next = text[i + 1] as string;
      if (next === 'N') {
        newLine('hard');
        i += 2;
        continue;
      }
      if (next === 'n') {
        newLine('soft');
        i += 2;
        continue;
      }
      if (next === 'h') {
        buf += ' ';
        i += 2;
        continue;
      }
      if (next === '{' || next === '}') {
        // A literal brace, escaped by the writer so it cannot open an override block.
        buf += next;
        i += 2;
        continue;
      }
      buf += ch;
      i++;
      continue;
    }

    if (ch === '{') {
      const close = text.indexOf('}', i);
      if (close === -1) {
        buf += ch;
        i++;
        continue;
      }
      const block = text.slice(i + 1, close);
      // A `{}` block containing no backslash is a COMMENT in ASS and is not rendered. Files
      // use it for translator notes. Dropping it silently is what every renderer does.
      if (block.includes('\\')) {
        flush();
        applyBlock(block);
      }
      i = close + 1;
      continue;
    }

    buf += ch;
    i++;
  }
  flush();
  lines.push(makeLine(runs, { breakBefore }));

  const granularity: TagParseOutput['granularity'] = anyKaraoke ? 'syllable' : 'coalesced';
  return {
    lines,
    position: positionTouched ? position : null,
    fade,
    wrap,
    karaokeKind,
    karaokeTicks,
    granularity,
    hasFragileTags: hasFragile,
    hasRelativeTimes: hasRelative || fade !== null || position.move !== null,
    unknownNames,
  };

  /*── tag application ───────────────────────────────────────────────────────────*/

  function applyBlock(block: string): void {
    for (const tag of splitTags(block)) applyTag(tag);
  }

  function applyTag(raw: string): void {
    const m = /^([a-zA-Z]+)(.*)$/s.exec(raw);
    if (m === null) return;
    const name = m[1] as string;
    const arg = (m[2] as string).trim();

    switch (name) {
      case 'b': {
        const n = Number(arg);
        if (!Number.isFinite(n)) return;
        // ASS overloads \b: 0/1 is a boolean, >= 100 is a numeric weight. A boolean cannot
        // carry \b700, which is why InlineStyle has both fields.
        state.style =
          n >= 100 ? { ...state.style, weight: n, bold: true } : set(state.style, 'bold', n === 1);
        return;
      }
      case 'i':
        state.style = set(state.style, 'italic', arg === '1');
        return;
      case 'u':
        state.style = set(state.style, 'underline', arg === '1');
        return;
      case 's':
        state.style = set(state.style, 'strikeOut', arg === '1');
        return;
      case 'bord':
        state.style = num(state.style, 'outlineWidth', arg);
        return;
      case 'shad':
        state.style = num(state.style, 'shadowDepth', arg);
        return;
      case 'be':
        state.style = num(state.style, 'edgeBlur', arg);
        return;
      case 'blur':
        state.style = num(state.style, 'blur', arg);
        return;
      case 'fn':
        state.style =
          arg === '' ? drop(state.style, 'fontName') : { ...state.style, fontName: arg };
        return;
      case 'fs':
        state.style = num(state.style, 'fontSize', arg);
        return;
      case 'fsp':
        state.style = num(state.style, 'spacing', arg);
        return;
      case 'fscx':
        state.style = num(state.style, 'scaleX', arg);
        return;
      case 'fscy':
        state.style = num(state.style, 'scaleY', arg);
        return;
      case 'fe':
        state.style = num(state.style, 'encoding', arg);
        return;
      case 'frx':
      case 'fry':
      case 'frz':
      case 'fr': {
        const n = Number(arg);
        if (!Number.isFinite(n)) return;
        const axis = name === 'frx' ? 'x' : name === 'fry' ? 'y' : 'z';
        state.style = { ...state.style, rotation: { ...(state.style.rotation ?? {}), [axis]: n } };
        return;
      }
      case 'fax':
      case 'fay': {
        const n = Number(arg);
        if (!Number.isFinite(n)) return;
        const axis = name === 'fax' ? 'x' : 'y';
        state.style = { ...state.style, shear: { ...(state.style.shear ?? {}), [axis]: n } };
        return;
      }
      case 'c':
      case '1c':
        setColor('primaryColor', arg);
        return;
      case '2c':
        setColor('secondaryColor', arg);
        return;
      case '3c':
        setColor('outlineColor', arg);
        return;
      case '4c':
        setColor('shadowColor', arg);
        return;
      case 'alpha': {
        const a = alphaOf(arg);
        if (a === null) return;
        state.alpha = { p: a, s: a, o: a, b: a };
        return;
      }
      case '1a':
      case '2a':
      case '3a':
      case '4a': {
        const a = alphaOf(arg);
        if (a === null) return;
        const key = name === '1a' ? 'p' : name === '2a' ? 's' : name === '3a' ? 'o' : 'b';
        state.alpha = { ...state.alpha, [key]: a };
        return;
      }
      case 'an': {
        const n = Number(arg);
        if (n >= 1 && n <= 9) {
          position = { ...position, alignment: n as Alignment };
          positionTouched = true;
        }
        return;
      }
      case 'a': {
        const n = ssaAlignmentToNumpad(Number(arg));
        if (n !== null) {
          position = { ...position, alignment: n };
          positionTouched = true;
        }
        return;
      }
      case 'pos': {
        const nums = numbers(arg);
        if (nums.length < 2) return;
        position = { ...position, point: normPoint(nums[0] as number, nums[1] as number) };
        positionTouched = true;
        return;
      }
      case 'org': {
        const nums = numbers(arg);
        if (nums.length < 2) return;
        position = { ...position, rotationOrigin: normPoint(nums[0] as number, nums[1] as number) };
        positionTouched = true;
        return;
      }
      case 'move': {
        const nums = numbers(arg);
        if (nums.length < 4) return;
        const move: MoveSpec = {
          from: normPoint(nums[0] as number, nums[1] as number),
          to: normPoint(nums[2] as number, nums[3] as number),
          startOffset: nums.length >= 6 ? T.delta(Math.round((nums[4] as number) * MSTICKS)) : null,
          endOffset: nums.length >= 6 ? T.delta(Math.round((nums[5] as number) * MSTICKS)) : null,
        };
        position = { ...position, move };
        positionTouched = true;
        hasRelative = true;
        return;
      }
      case 'clip':
      case 'iclip': {
        const clip = parseClip(arg, name === 'iclip');
        if (clip === null) return;
        position = { ...position, clip };
        positionTouched = true;
        return;
      }
      case 'fad': {
        const nums = numbers(arg);
        if (nums.length < 2) return;
        fade = {
          simple: {
            inMs: T.delta(Math.round((nums[0] as number) * MSTICKS)),
            outMs: T.delta(Math.round((nums[1] as number) * MSTICKS)),
          },
          complex: null,
        };
        hasRelative = true;
        return;
      }
      case 'fade': {
        const nums = numbers(arg);
        if (nums.length < 7) return;
        fade = {
          simple: null,
          complex: {
            a1: nums[0] as number,
            a2: nums[1] as number,
            a3: nums[2] as number,
            t1: T.delta(Math.round((nums[3] as number) * MSTICKS)),
            t2: T.delta(Math.round((nums[4] as number) * MSTICKS)),
            t3: T.delta(Math.round((nums[5] as number) * MSTICKS)),
            t4: T.delta(Math.round((nums[6] as number) * MSTICKS)),
          },
        };
        hasRelative = true;
        return;
      }
      case 't': {
        const animation = parseAnimation(arg);
        if (animation === null) return;
        state.style = {
          ...state.style,
          animations: [...(state.style.animations ?? []), animation],
        };
        hasFragile = true;
        hasRelative = true;
        return;
      }
      case 'r': {
        // `\r` resets to the cue's style; `\rName` resets to a named one. It is a ROUND-TRIP
        // HINT: it changes nothing about how this run's own fields are read, because they are
        // resolved. What it does is clear the running state.
        const target = arg === '' ? null : (input.styleByName(arg)?.id ?? null);
        state.style = { reset: target };
        state.alpha = {};
        return;
      }
      case 'q': {
        const n = Number(arg);
        if (n >= 0 && n <= 3) wrap = WRAP_STYLES[n] ?? null;
        return;
      }
      case 'p': {
        const n = Number(arg);
        if (!Number.isFinite(n)) return;
        flushDrawingBoundary();
        state.inDrawing = n > 0;
        state.drawingScale = n > 0 ? n : 1;
        return;
      }
      case 'pbo': {
        const n = Number(arg);
        if (Number.isFinite(n)) state.drawingBaselineOffset = n;
        return;
      }
      case 'k':
      case 'K':
      case 'kf':
      case 'ko':
      case 'kt': {
        const n = Number(arg);
        if (!Number.isFinite(n)) return;
        anyKaraoke = true;
        if (name === 'kt') {
          // Absolute karaoke-clock reset, relative to the CUE start.
          cursor = (input.cueStart as number) + Math.round(n * CS);
          return;
        }
        const kind: RunTimeKind =
          name === 'k' ? 'karaoke-fill' : name === 'ko' ? 'karaoke-outline' : 'karaoke-sweep';
        // Cue.karaoke records the DOMINANT kind for derivation; a run's own timeKind always
        // wins at write time, so a cue mixing \k and \kf round-trips faithfully.
        karaokeKind ??= name === 'k' ? 'k' : name === 'ko' ? 'ko' : 'kf';
        pending = { kind, ticks: Math.round(n * CS) };
        return;
      }
      default: {
        const tag = unknownTagOf(name, arg);
        if (!unknownNames.includes(name)) unknownNames.push(name);
        state.style = {
          ...state.style,
          unknownTags: [...(state.style.unknownTags ?? []), tag],
        };
        if (tag.argsUnclassified) hasFragile = true;
        return;
      }
    }
  }

  function flushDrawingBoundary(): void {
    if (buf !== '') flush();
  }

  function setColor(
    key: 'primaryColor' | 'secondaryColor' | 'outlineColor' | 'shadowColor',
    arg: string,
  ): void {
    if (arg === '') {
      state.style = drop(state.style, key);
      return;
    }
    const c = parseAssColor(arg);
    if (c === null) return;
    // `\1c` carries NO alpha; keep whatever alpha is in force rather than forcing opaque.
    const alphaKey =
      key === 'primaryColor'
        ? 'p'
        : key === 'secondaryColor'
          ? 's'
          : key === 'outlineColor'
            ? 'o'
            : 'b';
    const pending$ = state.alpha[alphaKey as 'p' | 's' | 'o' | 'b'];
    state.style = { ...state.style, [key]: pending$ === undefined ? c : withAlpha(c, pending$) };
  }

  function normPoint(x: number, y: number): Point {
    return {
      x: resolution.x === 0 ? 0 : x / resolution.x,
      y: resolution.y === 0 ? 0 : y / resolution.y,
    };
  }

  function parseClip(arg: string, inverse: boolean): ClipSpec | null {
    const parts = splitArgs(arg);
    if (parts.length === 4 && parts.every((p) => /^-?[\d.]+$/.test(p.trim()))) {
      const n = parts.map((p) => Number(p));
      return {
        kind: 'rect',
        inverse,
        rect: {
          a: normPoint(n[0] as number, n[1] as number),
          b: normPoint(n[2] as number, n[3] as number),
        },
        drawing: null,
        scale: null,
      };
    }
    // `\clip(scale, drawing)` or `\clip(drawing)`. The path is preserved BYTE-EXACT and never
    // interpreted: interpreting it buys nothing and risks corrupting it.
    const scale = parts.length === 2 ? Number(parts[0]) : null;
    const drawing = (parts.length === 2 ? parts[1] : parts[0]) ?? '';
    if (drawing.trim() === '') return null;
    return {
      kind: 'vector',
      inverse,
      rect: null,
      drawing: drawing.trim(),
      scale: Number.isFinite(scale) ? scale : null,
    };
  }

  function parseAnimation(arg: string): Animation | null {
    const parts = splitArgs(arg);
    if (parts.length === 0) return null;
    const tail = parts[parts.length - 1] as string;
    const head = parts.slice(0, -1).map((p) => Number(p.trim()));
    let startOffset: TickDelta | null = null;
    let endOffset: TickDelta | null = null;
    let accel: number | null = null;
    if (head.length === 1) accel = head[0] as number;
    else if (head.length === 2) {
      startOffset = T.delta(Math.round((head[0] as number) * MSTICKS));
      endOffset = T.delta(Math.round((head[1] as number) * MSTICKS));
    } else if (head.length >= 3) {
      startOffset = T.delta(Math.round((head[0] as number) * MSTICKS));
      endOffset = T.delta(Math.round((head[1] as number) * MSTICKS));
      accel = head[2] as number;
    }
    // `\t` animates TO a set of modifiers. Nested `\t` is not animatable in ASS, so the
    // recursion the type forbids is also forbidden by the format.
    const inner = parseInnerStyle(tail);
    return { startOffset, endOffset, accel, to: inner };
  }

  /** The modifier list inside `\t(...)`, parsed as its own little tag stream. */
  function parseInnerStyle(body: string): InlineStyle {
    const saved = state.style;
    const savedAlpha = state.alpha;
    state.style = {};
    state.alpha = {};
    for (const tag of splitTags(body)) {
      // Nested \t is not a thing; guard so a malformed file cannot recurse.
      if (tag.startsWith('t')) continue;
      applyTag(tag);
    }
    const out = resolvedStyle();
    state.style = saved;
    state.alpha = savedAlpha;
    return out;
  }
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Helpers
───────────────────────────────────────────────────────────────────────────────────────*/

function continuesWord(runs: readonly TextRun[]): boolean {
  for (let i = runs.length - 1; i >= 0; i--) {
    const run = runs[i] as TextRun;
    if (run.kind === 'drawing') continue;
    if (run.text === '') continue;
    // A syllable continues the previous word unless that word ended with whitespace.
    return !/\s$/u.test(run.text);
  }
  return false;
}

function set<K extends keyof InlineStyle>(style: InlineStyle, key: K, value: boolean): InlineStyle {
  // `\i0` is not "no opinion" — it is "explicitly not italic", which must survive as a delta
  // over a Style whose own Italic is -1. Dropping the key would silently re-italicise.
  return { ...style, [key]: value } as InlineStyle;
}

function drop<K extends keyof InlineStyle>(style: InlineStyle, key: K): InlineStyle {
  const out = { ...style };
  delete out[key];
  return out;
}

function num<K extends keyof InlineStyle>(style: InlineStyle, key: K, arg: string): InlineStyle {
  const n = Number(arg);
  if (!Number.isFinite(n)) return style;
  return { ...style, [key]: n } as InlineStyle;
}

function withAlpha(c: Color, a: number): Color {
  return { r: c.r, g: c.g, b: c.b, a };
}

/** `&HFF&` → 0 (transparent) in model terms; ASS alpha is inverted. */
function alphaOf(arg: string): number | null {
  const s = arg.replace(/^&H?/i, '').replace(/&$/, '');
  if (!/^[0-9a-fA-F]{1,2}$/.test(s)) return null;
  return 1 - Number.parseInt(s, 16) / 255;
}

/** Splits `\i1\pos(1,2)\c&HFF&` into ['i1', 'pos(1,2)', 'c&HFF&'], respecting parentheses. */
export function splitTags(block: string): readonly string[] {
  const out: string[] = [];
  let depth = 0;
  let current = '';
  let started = false;
  for (let i = 0; i < block.length; i++) {
    const ch = block[i] as string;
    if (ch === '\\' && depth === 0) {
      if (started && current.trim() !== '') out.push(current);
      current = '';
      started = true;
      continue;
    }
    if (!started) continue;
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    current += ch;
  }
  if (started && current.trim() !== '') out.push(current);
  return out;
}

/** The comma-separated arguments of `name(a,b,c)`, respecting nesting. */
export function splitArgs(arg: string): readonly string[] {
  const body = arg.startsWith('(') ? arg.slice(1, arg.endsWith(')') ? -1 : undefined) : arg;
  const out: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of body) {
    if (ch === '(') depth++;
    if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && depth === 0) {
      out.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  out.push(current);
  return out.length === 1 && (out[0] as string).trim() === '' ? [] : out;
}

function numbers(arg: string): readonly number[] {
  return splitArgs(arg)
    .map((p) => Number(p.trim()))
    .filter((n) => Number.isFinite(n));
}

/**
 * An unmodelled tag, with its arguments TYPED where we can and flagged where we cannot.
 *
 * The alternative — Design-2's opaque `assRaw: string` — let a framerate conversion rescale
 * the dialogue and leave `\fad`/`\move`/`\t` times untouched: silent desync on the product's
 * headline feature, with no loss entry. `argsUnclassified` is what makes that impossible: an
 * unclassified tag on a retimed cue produces 'animation-time-not-rescaled'.
 */
function unknownTagOf(name: string, arg: string): UnknownTag {
  const parts = arg === '' ? [] : splitArgs(arg);
  const args: TagArg[] = parts.map((p) => {
    const t = p.trim();
    const n = Number(t);
    return Number.isFinite(n) && t !== ''
      ? ({ kind: 'number', value: n } as TagArg)
      : ({ kind: 'text', value: t } as TagArg);
  });
  // We cannot know whether a number inside an unrecognised tag is a time. Saying we can is
  // how a desync ships with no diagnostic.
  return { name, args, argsUnclassified: args.some((a) => a.kind === 'number') };
}
